import { useState, useEffect } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import api, { formatApiError, setAuthToken } from "../lib/api";
import { Warehouse, ArrowRight } from "@phosphor-icons/react";
import { toast } from "sonner";

const BG_IMG =
  "https://static.prod-images.emergentagent.com/jobs/4ca66b80-84fe-4a77-bfaf-1893009b9536/images/b6cd4dbd2b4e30f3e7cc1f0ea382b8593c628f42be12d56022165476a462dc26.png";

export default function Login() {
  const { user, login, refresh } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [needsSetup, setNeedsSetup] = useState(false);
  const [checkedBootstrap, setCheckedBootstrap] = useState(false);

  useEffect(() => {
    api.get("/auth/bootstrap-status")
      .then(({ data }) => setNeedsSetup(!!data.needs_setup))
      .catch(() => setNeedsSetup(false))
      .finally(() => setCheckedBootstrap(true));
  }, []);

  if (user) return <Navigate to="/" replace />;

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      if (needsSetup) {
        // Bootstrap first admin
        const { data } = await api.post("/auth/bootstrap-admin", {
          email, password, name: name || "Amministratore",
        });
        if (data.access_token) setAuthToken(data.access_token);
        await refresh();
        toast.success("Account amministratore creato. Benvenuto!");
        navigate("/");
      } else {
        await login(email, password);
        toast.success("Accesso effettuato");
        navigate("/");
      }
    } catch (err) {
      const msg = formatApiError(err);
      setError(msg);
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen grid grid-cols-1 lg:grid-cols-2 bg-white" data-testid="login-page">
      <div className="flex items-center justify-center p-8 lg:p-16 fade-up">
        <div className="w-full max-w-md">
          <div className="flex items-center gap-3 mb-12">
            <div className="w-10 h-10 bg-[#1D3557] text-white flex items-center justify-center">
              <Warehouse size={22} weight="bold" />
            </div>
            <div>
              <div className="font-display font-bold tracking-tight text-lg text-[#0A0A0A]">
                MAGAZZINO
              </div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-[#4B5563]">
                Gestione Industriale
              </div>
            </div>
          </div>

          <div className="mb-10">
            <div className="text-[10px] uppercase tracking-[0.2em] text-[#4B5563] mb-3">
              {needsSetup ? "Configurazione Iniziale" : "Area Riservata"}
            </div>
            <h1 className="font-display text-4xl sm:text-5xl font-bold tracking-tight text-[#0A0A0A] leading-[1.05]">
              {needsSetup ? (
                <>Crea il tuo<br />account amministratore.</>
              ) : (
                <>Accedi al tuo<br />pannello operativo.</>
              )}
            </h1>
            <p className="mt-4 text-sm text-[#4B5563] max-w-sm">
              {needsSetup
                ? "Sembra che il database sia vuoto. Crea il primo account amministratore per iniziare. Questa schermata appare solo la prima volta."
                : "Materie prime, semilavorati e prodotti finiti. Tracciabilità end-to-end con codici a barre EAN-13."}
            </p>
          </div>

          <form onSubmit={onSubmit} className="space-y-5">
            {needsSetup && (
              <div>
                <label className="text-[10px] uppercase tracking-[0.15em] font-semibold text-[#4B5563] block mb-2">
                  Nome
                </label>
                <input
                  data-testid="setup-name-input"
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="w-full border border-[#E5E7EB] bg-white px-4 py-3 text-sm focus:ring-2 focus:ring-[#1D3557] focus:border-transparent outline-none"
                  placeholder="Mario Rossi"
                />
              </div>
            )}
            <div>
              <label className="text-[10px] uppercase tracking-[0.15em] font-semibold text-[#4B5563] block mb-2">
                Email
              </label>
              <input
                data-testid="login-email-input"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="w-full border border-[#E5E7EB] bg-white px-4 py-3 font-mono-data text-sm focus:ring-2 focus:ring-[#1D3557] focus:border-transparent outline-none"
                placeholder="nome@azienda.it"
              />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-[0.15em] font-semibold text-[#4B5563] block mb-2">
                Password {needsSetup && <span className="text-[#4B5563] normal-case tracking-normal">(min. 6 caratteri)</span>}
              </label>
              <input
                data-testid="login-password-input"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                minLength={needsSetup ? 6 : undefined}
                className="w-full border border-[#E5E7EB] bg-white px-4 py-3 font-mono-data text-sm focus:ring-2 focus:ring-[#1D3557] focus:border-transparent outline-none"
                placeholder="••••••••"
              />
            </div>

            {error && (
              <div
                data-testid="login-error"
                className="bg-[#FBEDEE] text-[#E63946] px-4 py-3 text-sm font-medium border border-[#E63946]/20"
              >
                {error}
              </div>
            )}

            <button
              data-testid="login-submit-btn"
              type="submit"
              disabled={submitting}
              className="w-full flex items-center justify-center gap-2 bg-[#1D3557] hover:bg-[#2A4B7C] text-white font-display font-semibold uppercase tracking-wider text-sm py-4 transition-colors disabled:opacity-60"
            >
              {submitting
                ? (needsSetup ? "Creazione…" : "Accesso in corso…")
                : (needsSetup ? "Crea Account" : "Accedi")}
              {!submitting && <ArrowRight size={16} weight="bold" />}
            </button>
          </form>
        </div>
      </div>

      <div className="hidden lg:block relative overflow-hidden">
        <img
          src={BG_IMG}
          alt=""
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-black/10" />
        <div className="absolute bottom-12 left-12 right-12 text-white">
          <div className="text-[10px] uppercase tracking-[0.25em] mb-3 opacity-80">
            Batch 1100 Litri · EAN-13 · Scanner Gun
          </div>
          <div className="font-display text-3xl font-bold leading-tight max-w-md">
            Ogni litro prodotto, ogni componente consumato, tracciato in tempo reale.
          </div>
        </div>
      </div>
    </div>
  );
}
